import MessageStore from "components/ManagerInteractions/Stores/MessageStore";
import { action, computed, makeObservable, observable, runInAction } from "mobx";
import type {
    ResearchEmail,
    ResearchFeedbackDetail,
    ResearchFeedbackResponse,
    ResearchFeedbackSentiment,
    ResearchQuestion,
    ResearchRunDetail,
} from "models/ResearchFindings/ResearchFindings";
import ResearchFindingsService from "services/ResearchFindingsService";
import { BaseStore } from "stores/BaseStore";
import type { IRootStore } from "stores/RootStore";

export type QuestionFilter =
    | "All"
    | "Generated"
    | "Researched"
    | "Answered"
    | "Rejected"
    | "Feedback";

export const QUESTION_FILTERS: QuestionFilter[] = [
    "All",
    "Generated",
    "Researched",
    "Answered",
    "Rejected",
    "Feedback",
];

function matchesFilter(q: ResearchQuestion, filter: QuestionFilter): boolean {
    switch (filter) {
        case "All":
            return true;
        case "Generated":
            return q.status === "Generated";
        case "Researched":
            return q.status === "Researched";
        // "Answered" = a report/answer was produced (or already delivered to the client).
        case "Answered":
            return !!q.answer || q.status === "Delivered";
        case "Rejected":
            return q.status === "Rejected";
        case "Feedback":
            return (
                (q.feedbackSummary?.positiveCount ?? 0) +
                    (q.feedbackSummary?.negativeCount ?? 0) >
                0
            );
    }
}

/**
 * Per-run store for the Research Findings page. One instance is created lazily by the singleton
 * ResearchFindingsStore when a run is first selected (mirrors Client Brain's per-user
 * UserContextStore). Holds the heavy run detail so it is fetched on demand and dropped when the
 * singleton clears its map. Also owns the per-run UI state: active filter tab + email preview.
 */
export class ResearchFindingStore extends BaseStore {
    private readonly service: ResearchFindingsService;
    private readonly messageStore: MessageStore;

    readonly runId: string;
    @observable detail?: ResearchRunDetail;
    @observable isLoading = false;
    @observable activeFilter: QuestionFilter = "All";

    // Email preview dialog state.
    @observable emailQuestionId?: string;
    @observable email?: ResearchEmail;
    @observable isEmailLoading = false;
    @observable recipients: string[] = [];
    @observable isSending = false;

    constructor(
        runId: string,
        rootStore: IRootStore,
        service: ResearchFindingsService,
    ) {
        super("ResearchFindingStore");
        makeObservable(this);
        this.runId = runId;
        this.service = service;
        this.messageStore = rootStore.getStore(MessageStore);
    }

    @computed
    get questions(): ResearchQuestion[] {
        return this.detail?.questions ?? [];
    }

    @computed
    get filteredQuestions(): ResearchQuestion[] {
        return this.questions.filter((q) => matchesFilter(q, this.activeFilter));
    }

    /** Count of questions matching each filter, for the tab badges. */
    @computed
    get filterCounts(): Record<QuestionFilter, number> {
        const counts = {} as Record<QuestionFilter, number>;
        QUESTION_FILTERS.forEach((f) => {
            counts[f] = this.questions.filter((q) => matchesFilter(q, f)).length;
        });
        return counts;
    }

    @computed
    get answerCount(): number {
        return this.questions.filter((q) => !!q.answer).length;
    }

    private applyFeedbackResponse(
        questionId: string,
        response: ResearchFeedbackResponse,
    ) {
        const question = this.questions.find((q) => q.id === questionId);
        if (!question) return;
        question.feedback = response.feedback;
        question.feedbackSummary = response.feedbackSummary;
    }

    /** Deferred detail fetch — guards against duplicate/repeat loads. */
    @action
    async load() {
        if (this.detail || this.isLoading) return;
        this.isLoading = true;
        try {
            const detail = await this.service.getRun(this.runId);
            runInAction(() => {
                this.detail = detail ?? undefined;
            });
        } catch (err: unknown) {
            this.messageStore.logError(
                err instanceof Error
                    ? err.message
                    : "Failed to load research run",
            );
        } finally {
            runInAction(() => {
                this.isLoading = false;
            });
        }
    }

    @action
    setFilter(filter: QuestionFilter) {
        this.activeFilter = filter;
    }

    @action
    async submitFeedback(
        questionId: string,
        sentiment: ResearchFeedbackSentiment,
        text?: string,
    ) {
        try {
            const response = await this.service.submitQuestionFeedback(
                questionId,
                sentiment,
                text,
            );
            runInAction(() => {
                this.applyFeedbackResponse(questionId, response);
            });
            this.messageStore.logMessage("Feedback saved", "success");
        } catch (err: unknown) {
            this.messageStore.logError(
                err instanceof Error ? err.message : "Failed to save feedback",
            );
        }
    }

    /** Fetches the full feedback history for a question (the "view all feedback" list). */
    async getQuestionFeedback(
        questionId: string,
    ): Promise<ResearchFeedbackDetail[]> {
        try {
            return await this.service.getQuestionFeedback(questionId);
        } catch (err: unknown) {
            this.messageStore.logError(
                err instanceof Error ? err.message : "Failed to load feedback",
            );
            return [];
        }
    }

    /** Deletes one of the current user's own feedback entries. Returns success. */
    async deleteQuestionFeedback(
        questionId: string,
        feedbackId: string,
    ): Promise<boolean> {
        try {
            const response = await this.service.deleteQuestionFeedback(
                questionId,
                feedbackId,
            );
            runInAction(() => {
                this.applyFeedbackResponse(questionId, response);
            });
            this.messageStore.logMessage("Feedback deleted", "success");
            return true;
        } catch (err: unknown) {
            this.messageStore.logError(
                err instanceof Error ? err.message : "Failed to delete feedback",
            );
            return false;
        }
    }

    // --- Client email preview / send ---

    @action
    async openEmail(questionId: string) {
        this.emailQuestionId = questionId;
        this.email = undefined;
        this.recipients = [];
        this.isEmailLoading = true;
        try {
            const email = await this.service.getQuestionEmail(questionId);
            runInAction(() => {
                this.email = email ?? undefined;
            });
        } catch (err: unknown) {
            this.messageStore.logError(
                err instanceof Error ? err.message : "Failed to load email",
            );
        } finally {
            runInAction(() => {
                this.isEmailLoading = false;
            });
        }
    }

    @action
    closeEmail() {
        this.emailQuestionId = undefined;
        this.email = undefined;
        this.recipients = [];
    }

    @action
    setRecipients(values: string[]) {
        this.recipients = values;
    }

    /**
     * Sends the report to the given recipients (the flushed chip-input list). An empty list
     * makes the server send to the caller themselves; that self-test send does NOT mark the
     * question delivered-to-client, so we only flip the status when recipients were given.
     */
    @action
    async sendEmail(recipients: string[]) {
        const questionId = this.emailQuestionId;
        const reportId = this.email?.reportId;
        if (!questionId || !reportId) {
            this.messageStore.logError(
                "This legacy report has no persisted email report ID and cannot be sent.",
            );
            return;
        }
        this.isSending = true;
        try {
            await this.service.sendQuestionEmail(questionId, recipients, reportId);
            runInAction(() => {
                if (recipients.length > 0) {
                    const question = this.questions.find(
                        (q) => q.id === questionId,
                    );
                    if (question) question.status = "Delivered";
                }
            });
            this.messageStore.logMessage("Report email sent", "success");
            this.closeEmail();
        } catch (err: unknown) {
            this.messageStore.logError(
                err instanceof Error ? err.message : "Failed to send email",
            );
        } finally {
            runInAction(() => {
                this.isSending = false;
            });
        }
    }
}

export default ResearchFindingStore;
